import { __awaiter } from "tslib";
import { SolidService } from './SolidService';
import { deleteSolidDataset, getSolidDataset, removeThing, saveSolidDatasetAt } from '@inrupt/solid-client';
import { RDFSerializer, Store, SPARQLDataDriver, } from '@openhps/rdf';
import { QueryEngine } from './QueryEngine';
export class SolidDataDriver extends SPARQLDataDriver {
    constructor(dataType, options) {
        var _a;
        super(dataType, options);
        this.options.engine = (_a = this.options.engine) !== null && _a !== void 0 ? _a : require('./engine-default')(); // eslint-disable-line
        if (this.options.engine) {
            this.engine = new QueryEngine(this.options.engine);
        }
        this.options.lenient = true;
        this.options.uriPrefix = this.options.uriPrefix || '/openhps';
        this.options.serialize = defaultThingSerializer;
        this.options.deserialize = defaultThingDeserializer;
        this.once('build', this._initService.bind(this));
    }
    _initService() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                if (!this.model) {
                    return resolve();
                }
                this.service = this.model.findService(SolidService);
                if (!this.service) {
                    return reject(new Error(`Unable to find SolidDataService!`));
                }
                resolve();
            });
        });
    }
    queryQuads(query, options, session) {
        if (session) {
            return super.queryQuads(query, Object.assign({ '@comunica/actor-http-inrupt-solid-client-authn:session': session, sources: [session.info.webId], lenient: true }, options));
        }
        else {
            return super.queryQuads(query, options);
        }
    }
    queryBindings(query, session, options) {
        if (session) {
            return super.queryBindings(query, Object.assign({ '@comunica/actor-http-inrupt-solid-client-authn:session': session, sources: [session.info.webId], lenient: true }, options));
        }
        else {
            return super.queryBindings(query, options);
        }
    }
    findByUID(id) {
        return new Promise((resolve, reject) => {
            this.service
                .findSessionByObjectUID(this.dataType, id)
                .then((session) => {
                return this.service.getThing(session, `/${this.options.uriPrefix}/${id}`);
            })
                .then((thing) => {
                const deserialized = this.options.deserialize(thing);
                resolve(deserialized);
            })
                .catch(reject);
        });
    }
    findOne(query, options = {}) {
        return this.service
            .findSessionByWebId(query.webId)
            .then((session) => {
            return this.service.getDataset(session, query.uri);
        })
            .then((dataset) => {
            if (!dataset) {
                return undefined;
            }
            const subjects = Object.values(dataset.graphs.default);
            const quads = RDFSerializer.subjectsToQuads(subjects);
            const store = new Store(quads);
            return super.findOne(query.query, options, {
                sources: [store],
                lenient: true,
            });
        });
    }
    findAll(query, options = {}) {
        return this.service
            .findSessionByWebId(query.webId)
            .then((session) => {
            return this.service.getDataset(session, query.uri);
        })
            .then((dataset) => {
            if (!dataset) {
                return [];
            }
            const subjects = Object.values(dataset.graphs.default);
            const quads = RDFSerializer.subjectsToQuads(subjects);
            const store = new Store(quads);
            return super.findAll(query.query, Object.assign({}, options), {
                sources: [store],
                lenient: true,
            });
        });
    }
    count(query) {
        return this.service
            .findSessionByWebId(query.webId)
            .then((session) => {
            return this.service.getDataset(session, query.uri);
        })
            .then((dataset) => {
            if (!dataset) {
                return 0;
            }
            const subjects = Object.values(dataset.graphs.default);
            const quads = RDFSerializer.subjectsToQuads(subjects);
            const store = new Store(quads);
            return super.count(query.query, {
                sources: [store],
                lenient: true,
            });
        });
    }
    insert(_, object) {
        return new Promise((resolve, reject) => {
            if (!object.webId) {
                if (this.service.session) {
                    object.webId = this.service.session.info.webId;
                }
                else {
                    return reject(new Error(`Unable to store data object or frame without WebID!`));
                }
            }
            this.service
                .findSessionByWebId(object.webId)
                .then((session) => __awaiter(this, void 0, void 0, function* () {
                if (!session) {
                    reject(new Error(`Unable to find solid session for ${object.webId}!`));
                    return;
                }
                if (!session.info.isLoggedIn) {
                    reject(new Error(`Solid session is not logged in!`));
                    return;
                }
                // Link the object
                this.service.linkSession(object, session.info.sessionId, this.dataType);
                const podURL = yield this.service.getPodUrl(session);
                const items = this.options.serialize(object, podURL);
                if (items.length === 0) {
                    reject(new Error(`Unable to serialize object to RDF!`));
                    return;
                }
                const documentURL = new URL(items[0].url);
                documentURL.hash = '';
                yield this.linkObjectToSession(object.uid, documentURL.href, session, this.dataType);
                this.service
                    .getDataset(session, documentURL.href)
                    .then((dataset) => {
                    if (!dataset) {
                        dataset = this.service.createDataset();
                    }
                    let promise = Promise.resolve(dataset);
                    for (let i = 0; i < items.length; i++) {
                        promise = promise.then((dataset) => new Promise((resolve, reject) => {
                            this.service
                                .createThing(session, items[i], dataset)
                                .then((dataset) => {
                                resolve(dataset);
                            })
                                .catch(reject);
                        }));
                    }
                    return promise;
                })
                    .then((dataset) => {
                    return this.service.saveDataset(session, documentURL.href, dataset);
                })
                    .then(() => {
                    resolve(object);
                })
                    .catch(reject);
            }))
                .catch(reject);
        });
    }
    delete(id) {
        return new Promise((resolve, reject) => {
            let uri;
            let currentSession;
            this.service
                .findSessionByObjectUID(this.dataType, id)
                .then((session) => __awaiter(this, void 0, void 0, function* () {
                if (!session) {
                    reject(new Error(`Unable to find solid session for ${this.dataType.name} with id '${id}'!`));
                    return;
                }
                currentSession = session;
                uri = yield this.findObjectURI(id, session, this.dataType);
                // Unlink the object
                this.service.unlinkSession(id, this.dataType);
                yield this.unlinkObjectFromSession(id, session, this.dataType);
                // Delete from Pod
                return getSolidDataset(uri, {
                    fetch: session.fetch,
                });
            }))
                .then((dataset) => {
                const graphs = Object.values(dataset.graphs.default);
                if (graphs[0].url === uri) {
                    // Delete complete graph when url is the dataset
                    return deleteSolidDataset(uri, {
                        fetch: currentSession.fetch,
                    });
                }
                else {
                    // Delete a thing within the dataset when URL is not the dataset
                    dataset = removeThing(dataset, uri);
                    return saveSolidDatasetAt(uri, dataset, {
                        fetch: currentSession.fetch,
                    });
                }
            })
                .then(() => resolve())
                .catch(reject);
        });
    }
    deleteAll() {
        throw new Error(`Not supported with SolidDataDriver!`);
    }
    findObjectURI(id, session, type) {
        return new Promise((resolve, reject) => {
            const prefix = `${SolidService.PREFIX}:${type.name}`;
            const key = `${prefix}:${session.info.sessionId}:${id}`;
            this.service.storage
                .get(key)
                .then((value) => resolve(value))
                .catch(reject);
        });
    }
    linkObjectToSession(id, uri, session, type) {
        return new Promise((resolve, reject) => {
            const prefix = `${SolidService.PREFIX}:${type.name}`;
            const key = `${prefix}:${session.info.sessionId}:${id}`;
            this.service.storage.set(key, uri).then(resolve).catch(reject);
        });
    }
    unlinkObjectFromSession(id, session, type) {
        return new Promise((resolve, reject) => {
            const prefix = `${SolidService.PREFIX}:${type.name}`;
            const key = `${prefix}:${session.info.sessionId}:${id}`;
            this.service.storage.delete(key).then(resolve).catch(reject);
        });
    }
}
/**
 * @param object
 * @param baseURI
 */
export function defaultThingSerializer(object, baseURI) {
    return RDFSerializer.serializeToSubjects(object, baseURI);
}
/**
 * @param thing
 */
export function defaultThingDeserializer(thing) {
    return RDFSerializer.deserializeFromSubjects(thing.url, [thing]);
}
//# sourceMappingURL=SolidDataDriver.js.map